"use client";

import { ToggleGroup as ToggleGroupPrimitive } from "@base-ui/react/toggle-group";
import { type VariantProps } from "class-variance-authority";
import * as React from "react";

import { Toggle, toggleVariants } from "@/components/ui/toggle";
import { cn } from "@/utils/lib";
import { resolveClassName } from "@/utils/resolve-class-name";

const ToggleGroupContext = React.createContext<
  VariantProps<typeof toggleVariants>
>({
  size: "default",
  variant: "default",
});

function ToggleGroup({
  className,
  defaultValue,
  onValueChange,
  type = "multiple",
  value,
  variant,
  size,
  children,
  ...props
}: Omit<
  React.ComponentProps<typeof ToggleGroupPrimitive<string>>,
  "defaultValue" | "multiple" | "onValueChange" | "value"
> &
  VariantProps<typeof toggleVariants> & {
    defaultValue?: string | string[];
    onValueChange?: (value: string | string[]) => void;
    type?: "multiple" | "single";
    value?: string | string[];
  }) {
  const multiple = type === "multiple";

  const normalizeValue = React.useCallback((input?: string | string[]) => {
    if (input === undefined) {
      return undefined;
    }

    if (Array.isArray(input)) {
      return input;
    }

    return input ? [input] : [];
  }, []);

  return (
    <ToggleGroupPrimitive
      data-slot="toggle-group"
      data-size={size}
      data-variant={variant}
      defaultValue={normalizeValue(defaultValue)}
      multiple={multiple}
      className={(state) =>
        cn(
          `
            group/toggle-group flex w-fit items-center rounded-md
            data-[variant=outline]:shadow-xs
          `,
          resolveClassName(className, state),
        )
      }
      onValueChange={(nextValue) => {
        onValueChange?.(multiple ? [...nextValue] : (nextValue[0] ?? ""));
      }}
      value={normalizeValue(value)}
      {...props}
    >
      <ToggleGroupContext.Provider value={{ variant, size }}>
        {children}
      </ToggleGroupContext.Provider>
    </ToggleGroupPrimitive>
  );
}

function ToggleGroupItem({
  className,
  children,
  variant,
  size,
  ...props
}: React.ComponentProps<typeof Toggle> & VariantProps<typeof toggleVariants>) {
  const context = React.useContext(ToggleGroupContext);

  return (
    <Toggle
      data-slot="toggle-group-item"
      data-size={context.size || size}
      data-variant={context.variant || variant}
      className={(state) =>
        cn(
          toggleVariants({
            variant: context.variant || variant,
            size: context.size || size,
          }),
          `
            min-w-0 flex-1 shrink-0 rounded-none shadow-none
            first:rounded-l-md
            last:rounded-r-md
            focus:z-10
            focus-visible:z-10
            data-[variant=outline]:border-l-0
            data-[variant=outline]:first:border-l
          `,
          resolveClassName(className, state),
        )
      }
      {...props}
    >
      {children}
    </Toggle>
  );
}

export { ToggleGroup, ToggleGroupItem };
